/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment } from 'react';
import { RouteComponent } from 'react-resource-router';
import { Panel, PanelSplitter, Aside } from '@atlaskit/navigation-system';
import { usePanelController } from '../../contexts/PanelContext';
import { cssMap, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- The ADS splitter is portaled and does not expose bounded style overrides.
import { css, Global } from '@emotion/react';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useLeftSidebarController } from '../../contexts/LeftSidebarContext';
import { useAsideController } from '../../contexts/AsideContext';

const styles = cssMap({
	mainRow: {
		display: 'flex',
		alignItems: 'stretch',
		height: '100%',
		minHeight: '0px',
	},
	leftColumn: {
		flexShrink: 0,
		minHeight: '100vh',
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	mainColumn: {
		flexGrow: 1,
		minWidth: '0px',
		height: '100%',
		minHeight: '0px',
	},
	resizeHandle: {
		width: '4px',
		cursor: 'col-resize',
		alignSelf: 'stretch',
		backgroundColor: token('elevation.surface.sunken'),
	},
});

const darkPanelSplitterStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The ADS splitter is rendered through a portal outside the panel content.
	'[data-testid="query-builder-dark-resize-container"]': {
		insetBlockStart: token('space.200'),
		insetBlockEnd: token('space.200'),
		insetInlineStart: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The ADS splitter is rendered through a portal outside the panel content.
	'[data-testid="twg-labs-dark-panel-resize-container"], [data-testid="twg-labs-dark-aside-resize-container"]':
		{
			insetBlockStart: token('space.200'),
			insetBlockEnd: token('space.200'),
			insetInlineStart: token('space.100'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The ADS splitter does not expose a visual style override.
	'[data-testid="query-builder-dark-resize"]': {
		color: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Keep the resize line hidden for pointer interaction.
		'&:hover, &:active': {
			color: 'transparent',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Keyboard users still need a visible focus indicator.
		'&:focus-within': {
			color: token('color.border.focused'),
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The ADS splitter does not expose a visual style override.
	'[data-testid="twg-labs-dark-panel-resize"], [data-testid="twg-labs-dark-aside-resize"]': {
		color: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Keep the resize line hidden for pointer interaction.
		'&:hover, &:active': {
			color: 'transparent',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Keyboard users still need a visible focus indicator.
		'&:focus-within': {
			color: token('color.border.focused'),
		},
	},
});

const isDarkWorkbenchOwner = (owner?: string): boolean =>
	owner === 'query-builder-dark' || owner === 'twg-labs-dark';

type RightSidePanelProps = {
	testId?: string;
};

export const RightSidePanel = ({
	testId = 'nav-right-side-panel',
}: RightSidePanelProps): JSX.Element | null => {
	const { isOpen, content, owner } = usePanelController();
	if (!isOpen) {
		return null;
	}
	return (
		<Fragment>
			{isDarkWorkbenchOwner(owner) ? <Global styles={darkPanelSplitterStyles} /> : null}
			<Panel
				key={owner}
				defaultWidth={isDarkWorkbenchOwner(owner) ? 452 : 600}
				hasBorder={!isDarkWorkbenchOwner(owner)}
				testId={testId}
			>
				<PanelSplitter
					label="Resize panel"
					testId={
						owner === 'query-builder-dark'
							? 'query-builder-dark-resize'
							: owner === 'twg-labs-dark'
								? 'twg-labs-dark-panel-resize'
								: undefined
					}
				/>
				{content}
			</Panel>
		</Fragment>
	);
};

type RightAsidePanelProps = {
	testId?: string;
};

export const RightAsidePanel = ({
	testId = 'nav-right-aside-panel',
}: RightAsidePanelProps): JSX.Element | null => {
	const { isOpen, content, owner } = useAsideController();
	if (!isOpen || !content) {
		return null;
	}
	return (
		<Fragment>
			{owner === 'twg-labs-dark' ? <Global styles={darkPanelSplitterStyles} /> : null}
			<Aside key={owner} defaultWidth={owner === 'twg-labs-dark' ? 452 : 500} testId={testId}>
				<PanelSplitter
					label="Resize chat"
					testId={owner === 'twg-labs-dark' ? 'twg-labs-dark-aside-resize' : undefined}
				/>
				{content}
			</Aside>
		</Fragment>
	);
};

type LeftSidePanelProps = {
	testId?: string;
};

export const LeftSidePanel = ({
	testId = 'nav-left-side-panel',
}: LeftSidePanelProps): JSX.Element | null => {
	const { content } = useLeftSidebarController();
	const [sidebarWidth, setSidebarWidth] = React.useState(460);

	if (!content) {
		return null;
	}

	// temporary hack until I redo this with a proper ADS sidebar.
	const handleResizeMouseDown = (e: React.MouseEvent) => {
		const startX = e.clientX;
		const startWidth = sidebarWidth;

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const delta = moveEvent.clientX - startX;
			const nextWidth = Math.min(640, Math.max(420, startWidth + delta));
			setSidebarWidth(nextWidth);
		};

		const handleMouseUp = () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
	};

	return (
		<Fragment>
			<Box xcss={styles.leftColumn} style={{ width: sidebarWidth }} testId={testId}>
				{content}
			</Box>
			<Box xcss={styles.resizeHandle} onMouseDown={handleResizeMouseDown} />
		</Fragment>
	);
};

type MainWithLeftSidebarProps = {
	testId?: string;
};

export const MainWithLeftSidebar = ({
	testId = 'nav-main-with-left-sidebar',
}: MainWithLeftSidebarProps): JSX.Element | null => {
	return (
		<Box xcss={styles.mainRow} testId={testId}>
			<LeftSidePanel />
			<Box xcss={styles.mainColumn}>
				<RouteComponent />
			</Box>
		</Box>
	);
};
